/**
 * API client for FarmFleet backend
 * Uses localhost in development, production URL in production
 */
const PRODUCTION_API_URL = 'https://api.farmfleetz.org';
const isLocalDev =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE_URL = isLocalDev ? 'http://localhost:5000' : (process.env.REACT_APP_API_URL || PRODUCTION_API_URL);

export async function fetchApi(path, options = {}) {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `API error: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  getHealth: () => fetchApi('/api/health'),
  getWelcome: () => fetchApi('/api'),
  login: (username, password) =>
    fetchApi('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  // Orders
  getOrders: (status) =>
    fetchApi(status ? `/api/orders?status=${status}` : '/api/orders'),
  getOrder: (id) => fetchApi(`/api/orders/${id}`),
  updateOrderStatus: (id, status) =>
    fetchApi(`/api/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  // Trips
  getTrips: (status) =>
    fetchApi(status ? `/api/trips?status=${status}` : '/api/trips'),
  getTrip: (id) => fetchApi(`/api/trips/${id}`),
  createTrip: (order_id, driver_id) =>
    fetchApi('/api/trips', {
      method: 'POST',
      body: JSON.stringify({ order_id, driver_id }),
    }),
  updateTrip: (id, data) =>
    fetchApi(`/api/trips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  skipTripStop: (tripId, stopId) =>
    fetchApi(`/api/trips/${tripId}/stops/${stopId}/skip`, {
      method: 'POST',
      body: '{}',
    }),

  // Drivers
  getDrivers: () => fetchApi('/api/drivers'),
  getDriver: (id) => fetchApi(`/api/drivers/${id}`),
  createDriver: (name, phone, extra = {}) =>
    fetchApi('/api/drivers', {
      method: 'POST',
      body: JSON.stringify({ name, phone, ...extra }),
    }),
  updateDriver: (id, data) =>
    fetchApi(`/api/drivers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteDriver: (id) =>
    fetchApi(`/api/drivers/${id}`, { method: 'DELETE' }),

  // Farmers
  getFarmers: () => fetchApi('/api/farmers'),
  getFarmer: (id) => fetchApi(`/api/farmers/${id}`),
  createFarmer: (name, phone, location) =>
    fetchApi('/api/farmers', {
      method: 'POST',
      body: JSON.stringify({ name, phone, location }),
    }),
  updateFarmer: (id, data) =>
    fetchApi(`/api/farmers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteFarmer: (id) =>
    fetchApi(`/api/farmers/${id}`, { method: 'DELETE' }),

  // Activity
  getActivity: (limit, opsOnly, coreOnly) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (coreOnly) params.set('core', '1');
    else if (opsOnly) params.set('ops', '1');
    const qs = params.toString();
    return fetchApi(qs ? `/api/activity?${qs}` : '/api/activity');
  },

  // Auth
  changePassword: (username, currentPassword, newPassword) =>
    fetchApi('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ username, currentPassword, newPassword }),
    }),

  // Analytics
  getAnalytics: () => fetchApi('/api/analytics'),

  // Pricing (quote without creating an order)
  postPricingQuote: (body) =>
    fetchApi('/api/pricing/quote', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Pricing Grid V1
  getPricingConfig: () => fetchApi('/api/pricing/config'),
  savePricingConfig: (config) =>
    fetchApi('/api/pricing/config', { method: 'PUT', body: JSON.stringify(config) }),
  verifyPricing: () => fetchApi('/api/pricing/verify'),

  // Catalog — service areas & products
  getServiceAreas: () => fetchApi('/api/catalog/areas'),
  updateServiceArea: (id, data) =>
    fetchApi(`/api/catalog/areas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(typeof data === 'boolean' ? { active: data } : data),
    }),
  getProducts: () => fetchApi('/api/catalog/products'),

  // Public contact form
  submitContactForm: (body) =>
    fetchApi('/api/contact', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Admin inbox
  getMessages: () => fetchApi('/api/messages'),
  getMessagesUnreadCount: () => fetchApi('/api/messages/unread-count'),
  getMessage: (id) => fetchApi(`/api/messages/${id}`),
  markMessageRead: (id) =>
    fetchApi(`/api/messages/${id}/read`, { method: 'PATCH', body: '{}' }),
  deleteMessage: (id) =>
    fetchApi(`/api/messages/${id}`, { method: 'DELETE' }),

  // Clusters
  getClusters: (date, status) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (status) params.set('status', status);
    const qs = params.toString();
    return fetchApi(qs ? `/api/clusters?${qs}` : '/api/clusters');
  },
  recomputeClusters: () =>
    fetchApi('/api/clusters/recompute', { method: 'POST', body: '{}' }),

  // Routes
  previewRoute: (body) =>
    fetchApi('/api/routes/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createRoute: (body) =>
    fetchApi('/api/routes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRoutes: (status) =>
    fetchApi(status ? `/api/routes?status=${encodeURIComponent(status)}` : '/api/routes'),
  getRoute: (id) => fetchApi(`/api/routes/${id}`),
  acceptRoute: (id, driver_id) =>
    fetchApi(`/api/routes/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ driver_id }),
    }),
  cancelRoute: (id) =>
    fetchApi(`/api/routes/${id}/cancel`, { method: 'POST', body: '{}' }),

  // Roads
  getRoads: () => fetchApi('/api/roads'),
  createRoad: (body) =>
    fetchApi('/api/roads', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRoad: (id, data) =>
    fetchApi(`/api/roads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Fleet
  getFleetLive: () => fetchApi('/api/fleet/live'),
  getFleetUtilization: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchApi(qs ? `/api/fleet/utilization?${qs}` : '/api/fleet/utilization');
  },
};
